import { useEffect, useState } from "react";
import { SettingsCard } from "../../../../../layout/portalPageLayout";
import { H3, H5, H6 } from "../../../../../components/Typography/Typography";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { Row } from "../../../../../components/Layout/Layout";
import {
  CancelButtonSmall,
  EditButton,
  PrimaryButtonMedium,
} from "../../../../../components/Buttons/Buttons";
import { ToggleSwitchV2 } from "../../../../../components/ToggleSwitch/ToggleSwitch";
import { SelectBoxV2 } from "../../../../../components/SelectBoxV2/SelectBoxV2";
import { useStoreState } from "../../../../../util/util";
import { screenSize } from "../../../../../theme";
import { REMINDER_SELECT_BOX_OPTIONS } from "./ReminderNotifications";
import useSWR from "swr";
import { DelayedSpinner } from "../../../../../components/DelayedSpinner/DelayedSpinner";
import { ErrorPlaceholder } from "../../../../../components/Error";
import type {
  OptionType,
  StorefrontEmailReminderNumberRange,
  StorefrontProductEmailReminder,
} from "../../../../../types/types";
import type { FormState } from "react-hook-form";
import axios from "axios";
import { useNotifications } from "../../../../../components/Notifications/NotificationsContext";
import { spacingTokens } from "../../../../../designTokens";

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.resolve("spacing_3xl")};
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  display: flex;
  flex-direction: column;
`;

const ReminderContainer = styled.div`
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  display: flex;
  flex-direction: column;
  width: 50%;
  @media ${screenSize.medium} {
    width: 100%;
  }
`;

export function PricingReminders() {
  const { t } = useTranslation();
  const { storefront_id } = useStoreState();
  const { notifySuccess, notifyError } = useNotifications();
  const [editMode, setEditMode] = useState(false);

  // Fetch price tier reminders
  const {
    data: reminders,
    error,
    mutate,
  } = useSWR<StorefrontProductEmailReminder[]>(
    `/v2/storefronts/${storefront_id}/price-tiers-reminders`
  );

  // Get the first price tier reminder if available
  const priceTierReminder = reminders?.find(
    (reminder) => reminder.reminder_type === "price_tier_expiry"
  );

  const [enabled, setEnabled] = useState({
    previous: priceTierReminder?.enabled ?? false,
    current: priceTierReminder?.enabled ?? false,
  });

  const [recipients, setRecipients] = useState({
    previous: {
      customer: priceTierReminder?.send_to_buyers ?? true,
      distributor: priceTierReminder?.send_to_distributors ?? false,
    },
    current: {
      customer: priceTierReminder?.send_to_buyers ?? true,
      distributor: priceTierReminder?.send_to_distributors ?? false,
    },
  });
  const [days, setDays] = useState({
    previous: priceTierReminder?.first_reminder ?? 1,
    current: priceTierReminder?.first_reminder ?? 1,
  });
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = async () => {
    const payload = {
      enabled: enabled.current,
      send_to_buyers: recipients.current.customer,
      send_to_distributors: recipients.current.distributor,
      first_reminder: days.current,
      reminder_type: "price_tier_expiry",
    };
    setIsLoading(true);
    try {
      await axios.put(
        `/v2/storefronts/${storefront_id}/price-tiers-reminders`,
        { reminder_data: [payload] }
      );
      setEditMode(false);
      await mutate();
      notifySuccess(t("Pricing reminders updated successfully."));
    } catch (error) {
      notifyError(t("Failed to update pricing reminders."));
    } finally {
      setIsLoading(false);
    }
  };

  const onCancel = () => {
    setEnabled(({ previous }) => ({ previous, current: previous }));
    setRecipients(({ previous }) => ({ previous, current: previous }));
    setDays(({ previous }) => ({ previous, current: previous }));
    setEditMode(false);
  };

  useEffect(() => {
    if (priceTierReminder) {
      setEnabled({
        previous: priceTierReminder.enabled,
        current: priceTierReminder.enabled,
      });
      setRecipients({
        previous: {
          customer: priceTierReminder.send_to_buyers,
          distributor: priceTierReminder.send_to_distributors,
        },
        current: {
          customer: priceTierReminder.send_to_buyers,
          distributor: priceTierReminder.send_to_distributors,
        },
      });
      setDays({
        previous: priceTierReminder.first_reminder ?? 1,
        current: priceTierReminder.first_reminder ?? 1,
      });
    }
  }, [priceTierReminder]);

  // Show loading state
  if (!reminders && !error) {
    return <DelayedSpinner />;
  }

  // Show error state
  if (error) {
    return <ErrorPlaceholder message={error.message} />;
  }

  return (
    <SettingsCard>
      <Row>
        <H3>{t("Pricing Reminders")}</H3>
        {!editMode && (
          <EditButton
            testid={"edit-pricing-reminders"}
            onClick={() => setEditMode(true)}
            title={t("Edit Pricing Reminders")}
          />
        )}
        {editMode && <CancelButtonSmall onClick={onCancel} />}
      </Row>
      <p style={{ marginTop: spacingTokens.spacing_none }}>
        {t(
          "Enable or disable reminders based on your preferences, and configure when they will be sent."
        )}
        <br />
        {t(
          "You can set reminders to trigger a specific number of days after an event."
        )}
      </p>
      {/* 1. Expiring Price Tiers Toggle */}
      <Section>
        <H5>{t("Expiring Price Tiers")}</H5>
        <ToggleSwitchV2
          label={t("Enable reminders for expiring price tiers")}
          name="enable_expiring_price_tiers"
          disabled={!editMode}
          checked={enabled.current}
          onChange={(e) =>
            setEnabled(({ current }) => ({
              previous: current,
              current: e.target.checked,
            }))
          }
        />
      </Section>
      {/* 2. Recipients Section */}
      {editMode ||
      recipients.current.customer ||
      recipients.current.distributor ? (
        <Section>
          <H6>{t("Recipients")}</H6>
          {!editMode ? (
            <>
              {recipients.current.customer && <div>{t("Customers")}</div>}
              {recipients.current.distributor && <div>{t("Distributors")}</div>}
            </>
          ) : (
            <>
              <ToggleSwitchV2
                label={t("Customers")}
                name="recipient_customer"
                checked={recipients.current.customer}
                onChange={(e) =>
                  setRecipients(({ current }) => ({
                    previous: current,
                    current: { ...current, customer: e.target.checked },
                  }))
                }
                style={{ marginRight: 16 }}
              />
              <ToggleSwitchV2
                label={t("Distributors")}
                name="recipient_distributor"
                checked={recipients.current.distributor}
                onChange={(e) =>
                  setRecipients(({ current }) => ({
                    previous: current,
                    current: { ...current, distributor: e.target.checked },
                  }))
                }
              />
            </>
          )}
        </Section>
      ) : (
        <></>
      )}
      {/* 3. Days Before Expiration */}
      <Section>
        <H6>{t("Days before expiration")}</H6>
        {!editMode ? (
          <div>
            {REMINDER_SELECT_BOX_OPTIONS.find(
              (opt) => opt.value === days.current
            )?.label || "--"}
          </div>
        ) : (
          <ReminderContainer>
            <SelectBoxV2
              options={REMINDER_SELECT_BOX_OPTIONS}
              value={
                REMINDER_SELECT_BOX_OPTIONS.find(
                  (opt) => opt.value === days.current
                ) || null
              }
              onChange={(
                opt: OptionType<StorefrontEmailReminderNumberRange> | null
              ) => {
                setDays(({ current }) => ({
                  previous: current,
                  current: opt?.value ?? 1,
                }));
              }}
              placeholder={t("Select days")}
              name="days"
              clearable={false}
              formState={{ submitCount: 1 } as FormState<Record<string, any>>}
              errors={{}}
            />
            <PrimaryButtonMedium
              style={{ width: "180px" }}
              onClick={onSubmit}
              loading={isLoading}
              type="button"
            >
              {t("Save your changes")}
            </PrimaryButtonMedium>
          </ReminderContainer>
        )}
      </Section>
    </SettingsCard>
  );
}
